import React from 'react';
import {observer} from 'mobx-react';
import {requireImage} from '../../utils/utils';
import {resolveLogoUrl} from '../../utils/logo-url.utils';
import {RolesConstant} from '@techsee/techsee-common/lib/constants/account.constants';
import {ConnectivityController} from './ConnectivityController';
import {ChangeSettingsModal} from '../../modals/change-settings-modal/ChangeSettingsModal.component';
import {ChangeAccountModal} from '../../modals/change-account-modal/ChangeAccountModal.component';
import get from 'lodash/get';
import includes from 'lodash/includes';

interface ConnectivityLayoutProps {
    children: React.ReactNode;
    controller: ConnectivityController;
}

interface ConnectivityLayoutState {
    isDropdownOpen: boolean;
}

@observer
export class ConnectivityLayout extends React.Component<ConnectivityLayoutProps, ConnectivityLayoutState> {
    private dropdownRef = React.createRef<HTMLSpanElement>();

    constructor(props: ConnectivityLayoutProps) {
        super(props);
        this.state = {
            isDropdownOpen: false
        };
    }

    componentDidMount(): void {
        document.addEventListener('click', this.handleClickOutside);
    }

    componentWillUnmount(): void {
        document.removeEventListener('click', this.handleClickOutside);
    }

    private handleClickOutside = (event: MouseEvent): void => {
        if (this.dropdownRef.current && !this.dropdownRef.current.contains(event.target as Node)) {
            this.setState({isDropdownOpen: false});
        }
    };

    private toggleDropdown = (): void => {
        this.setState((prevState) => ({isDropdownOpen: !prevState.isDropdownOpen}));
    };

    render(): JSX.Element {
        const {controller} = this.props;
        const {translate, dir, isMobileDevice, isIOS, useNewInterface, accountData, currentUser} = controller;

        const fullName = currentUser ? `${currentUser.firstName || ''} ${currentUser.lastName || ''}`.trim() : '';
        const logoUrl = get(accountData, 'settings.logoUrl', '');
        const useFallbackLogo = isMobileDevice || !logoUrl;
        const topbarTitle =
            currentUser?.role === RolesConstant.TECHNICIAN_SUPERVISOR
                ? 'INVITE.VIEW.VISUAL_SUPPORT_TS'
                : 'INVITE.VIEW.VISUAL_SUPPORT';
        const isSysOrSuperAdmin = currentUser
            ? [RolesConstant.SYSADMIN, RolesConstant.SUPERADMIN].includes(currentUser.role)
            : false;
        const terms = get(accountData, 'protectedSettings.terms', {});
        const version = get(accountData, 'version', '');
        const canRedirectToAdmin =
            currentUser &&
            accountData &&
            get(accountData, 'protectedSettings.enableLinkToAdmin', false) &&
            !includes([RolesConstant.TECHNICIAN, RolesConstant.TECHNICIAN_SUPERVISOR], currentUser.role);
        const isMultiAccountsUser = Boolean(currentUser?.parentSubUserId);

        const containerClasses = [
            'ts-container',
            'ts-invite',
            isIOS ? 'ts-mobile-ios' : '',
            useNewInterface ? 'bright-invite' : ''
        ]
            .filter(Boolean)
            .join(' ');

        return (
            <div className={containerClasses}>
                <div id='background-runner' />
                <div className='ts-header-wrapper'>
                    <header className='ts-topbar' dir={dir}>
                        <div className='logo'>
                            <img src={resolveLogoUrl(useFallbackLogo ? '' : logoUrl)} alt='logo' />
                        </div>
                        <div className='ts-topbar-content'>
                            <div className='ts-breadcrumbs' />
                            <span className='ts-topbar-title' style={{display: isMobileDevice ? 'none' : 'inline'}}>
                                {translate(topbarTitle)}
                            </span>
                            <span
                                ref={this.dropdownRef}
                                className='ts-user-badge pull-right'
                                dir='ltr'
                                style={{position: 'relative'}}>
                                <a
                                    href='#'
                                    id='user-badge'
                                    onClick={(e) => {
                                        e.preventDefault();
                                        this.toggleDropdown();
                                    }}>
                                    <small className='name-area'>
                                        {fullName.length > 20 ? `${fullName.substring(0, 20)}...` : fullName}
                                    </small>
                                    <i className='glyphicon glyphicon-cog' />
                                </a>
                                {this.state.isDropdownOpen && (
                                    <ul
                                        className='dropdown-menu'
                                        aria-labelledby='user-badge'
                                        style={{display: 'block'}}>
                                        {canRedirectToAdmin && (
                                            <li>
                                                <a
                                                    href='#'
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        this.setState({isDropdownOpen: false});
                                                        controller.handleAdminClick();
                                                    }}>
                                                    {translate('INVITE.VIEW.ADMIN')}
                                                </a>
                                            </li>
                                        )}
                                        <li>
                                            <a
                                                href='#'
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    this.setState({isDropdownOpen: false});
                                                    controller.handleSettingsClick();
                                                }}>
                                                {translate('INVITE.VIEW.SETTINGS')}
                                            </a>
                                        </li>
                                        {isMultiAccountsUser && (
                                            <li>
                                                <a
                                                    href='#'
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        this.setState({isDropdownOpen: false});
                                                        controller.handleSwitchAccountClick();
                                                    }}>
                                                    {translate('LOGIN.VIEW.SWITCH_ACCOUNT')}
                                                </a>
                                            </li>
                                        )}
                                        <li>
                                            <a
                                                href='#'
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    this.setState({isDropdownOpen: false});
                                                    controller.handleLogoutClick();
                                                }}>
                                                {translate('INVITE.VIEW.LOGOUT')}
                                            </a>
                                        </li>
                                    </ul>
                                )}
                            </span>
                        </div>
                    </header>
                </div>
                <div className='ts-main'>
                    <main>{this.props.children}</main>
                </div>
                <footer className='ts-footer not-mobile'>
                    {isSysOrSuperAdmin && version && (
                        <div className='version small-text'>Product Version: {version}</div>
                    )}
                    <small>{translate('INVITE.VIEW.POWERED_BY')}</small>&nbsp;
                    <img src={requireImage('new-logo-text-right.png')} alt='logo' />
                    {terms.display && terms.path && (
                        <span className='terms-link'>
                            <a href={terms.path} target='_blank' rel='noopener noreferrer'>
                                {translate('TERMS.TERMS_AND_CONDITIONS')}
                            </a>
                        </span>
                    )}
                </footer>
                {controller.changeSettingsModalController && (
                    <ChangeSettingsModal ctrl={controller.changeSettingsModalController!} />
                )}
                {controller.changeAccountModalController && (
                    <ChangeAccountModal ctrl={controller.changeAccountModalController!} />
                )}
            </div>
        );
    }
}

export default ConnectivityLayout;
